/**
 * ProcessDocumentsCard — lists the customer's uploaded profile documents and
 * lets them start (or retry) extraction themselves, then watch the status.
 *
 * Previously extraction was admin-triggered; now the user kicks it off. Each
 * row shows: not-yet-run → "Start extraction", running → "Processing…",
 * success → "Done", failed → error + "Retry". The list polls itself while any
 * run is in flight (see useProfileDocumentExtractions), and a successful run
 * invalidates the extracted-profile query so the matching tab fills in.
 */
import * as React from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  useToast,
} from '@finmin/ui';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Play,
  RotateCw,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  useDeleteProfileDocument,
  useProfileDocumentExtractions,
  useTriggerProfileExtraction,
  type ProfileDocumentExtractionItem,
} from '@/api/profile-documents';
import { formatDocumentType } from './format';

export function ProcessDocumentsCard() {
  const { toast } = useToast();
  const { data, isLoading, error } = useProfileDocumentExtractions();
  const trigger = useTriggerProfileExtraction({
    onError: (err) => {
      toast({
        title: "Couldn't start extraction",
        description: err.message,
        variant: 'error',
      });
    },
  });
  const remove = useDeleteProfileDocument({
    onSuccess: () => {
      toast({ title: 'Document deleted', variant: 'success' });
    },
    onError: (err) => {
      toast({
        title: "Couldn't delete document",
        description: err.message,
        variant: 'error',
      });
    },
  });

  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
            Your documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading your documents…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
            Your documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Couldn't load your documents. Try again later.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
          Your documents
        </CardTitle>
        <CardDescription>
          Start extraction to read each document and fill in the matching tab.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {items.map((item) => (
            <DocumentRow
              key={item.document_id}
              item={item}
              pending={
                trigger.isPending &&
                trigger.variables?.documentId === item.document_id
              }
              deleting={
                remove.isPending &&
                remove.variables?.documentId === item.document_id
              }
              onProcess={() =>
                trigger.mutate({ documentId: item.document_id })
              }
              onDelete={() =>
                remove.mutate({ documentId: item.document_id })
              }
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * Map a raw backend/extractor error into a short, non-technical message.
 *
 * Extraction runs an LLM under the hood, so failures are often transient
 * (provider overloaded / rate-limited / timed out) and worth retrying. We
 * never surface raw provider JSON like
 *   "503 UNAVAILABLE {'error': {'message': 'high demand' ...}}"
 * to customers — that lives in the title attribute for support only.
 */
function friendlyExtractionError(raw: string | null): {
  message: string;
  transient: boolean;
} {
  const text = (raw ?? '').toLowerCase();

  const isTransient =
    /\b503\b|\b429\b|unavailable|high demand|overload|rate.?limit|quota|temporarily|timed? ?out|timeout|deadline|try again/.test(
      text,
    );
  if (isTransient) {
    return {
      message:
        'Our extraction service is busy right now. Please try again in a moment.',
      transient: true,
    };
  }

  // Unreadable / unsupported document content (not a transient outage).
  if (/unsupported|not.?downloadable|corrupt|password|encrypted|empty|no text/.test(text)) {
    return {
      message:
        "We couldn't read this document. Make sure it's a clear, unlocked PDF and try again.",
      transient: false,
    };
  }

  return {
    message:
      "We couldn't process this document. Please try again — if it keeps failing, contact support.",
    transient: false,
  };
}

function DocumentRow({
  item,
  pending,
  deleting,
  onProcess,
  onDelete,
}: {
  item: ProfileDocumentExtractionItem;
  pending: boolean;
  deleting: boolean;
  onProcess: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = React.useState(false);
  const status = item.latest_run_status;
  // "In flight" covers an optimistic just-clicked trigger and a backend run
  // that is pending/running.
  const inFlight = pending || status === 'pending' || status === 'running';
  const failure =
    status === 'failed' ? friendlyExtractionError(item.latest_run_error) : null;

  return (
    <li className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <FileText className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">
          {item.document_label || formatDocumentType(item.document_type)}
        </p>
        {failure ? (
          // Friendly, non-technical message — the raw backend/LLM error is kept
          // in the title attribute for support/debugging only.
          <p className="text-xs text-destructive" title={item.latest_run_error ?? undefined}>
            {failure.message}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground truncate">
            {formatDocumentType(item.document_type)}
          </p>
        )}
      </div>

      {confirming ? (
        // Inline two-step confirm — deleting also removes any data extracted
        // from this document, so we make the customer confirm first.
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirming(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onDelete}
            disabled={deleting}
            className="gap-1.5"
          >
            {deleting ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            )}
            Delete
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-2">
          {!item.has_extractor ? (
            <Badge variant="outline" className="text-[10px]">
              Not supported yet
            </Badge>
          ) : inFlight ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Spinner className="h-3.5 w-3.5" /> Processing…
            </span>
          ) : status === 'success' ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> Done
            </span>
          ) : status === 'failed' ? (
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
              <Button size="sm" variant="outline" onClick={onProcess} className="gap-1.5">
                <RotateCw className="h-3.5 w-3.5" aria-hidden />
                Try again
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={onProcess} className="gap-1.5">
              <Play className="h-3.5 w-3.5" aria-hidden />
              Start extraction
            </Button>
          )}
          {/* Delete the document (and any data extracted from it). Disabled
              while an extraction run is in flight to avoid mid-run deletes. */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setConfirming(true)}
            disabled={inFlight}
            aria-label="Delete document"
            title="Delete document"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}
    </li>
  );
}
